import React, {useState, useRef, useEffect} from 'react';
import {Avatar, Box, Container, Center, Flex, HStack, Input, Spinner, VStack} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {TextG12, TextG14, TextG16} from '../../../components/typography';
import ConnectionCard from './ConnectionCard';
import {
  useIncomingFriendRequests,
  useOutgoingFriendRequests,
  useAcceptFriendRequest,
  useDeclineFriendRequest,
  useCancelFriendRequest,
  FriendRequest,
} from '../../../hooks/useFriendRequest';
import {useConnections} from '../../../hooks/useConnections';
import styled from 'styled-components';

type FilterTab = 'all' | 'close_neighbors' | 'shared_plans' | 'requests';

const TabButton = styled.button<{$active: boolean}>`
  padding: 6px 16px;
  border-radius: 20px;
  font-family: Gordita;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? '600' : '500')};
  color: ${(props) => (props.$active ? 'white' : '#4A4A4A')};
  background: ${(props) => (props.$active ? '#276ACD' : '#F4F4F4')};
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RequestsBadge = styled.span<{$active: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: ${(props) => (props.$active ? 'white' : '#E02F84')};
  color: ${(props) => (props.$active ? '#276ACD' : 'white')};
  font-family: Gordita;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`;

const ActionBtn = styled.button<{$variant: 'accept' | 'decline' | 'cancel'}>`
  padding: 8px 18px;
  border-radius: 20px;
  font-family: Gordita;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${(props) => (props.$variant === 'accept' ? 'white' : '#1A1A1A')};
  background: ${(props) => (props.$variant === 'accept' ? '#E02F84' : 'white')};
  border: ${(props) =>
    props.$variant === 'accept' ? 'none' : '1px solid #D5D5D5'};
  &:active {
    transform: scale(0.96);
  }
  &:disabled {
    background: #ECECEC;
    color: #B0B0B0;
    border-color: #ECECEC;
    cursor: not-allowed;
    transform: none;
  }
`;

const RequestCard = ({
  request,
  type,
  onAccept,
  onDecline,
  onCancel,
  isLoading,
}: {
  request: FriendRequest;
  type: 'incoming' | 'outgoing';
  onAccept?: (id: number) => void;
  onDecline?: (id: number) => void;
  onCancel?: (id: number) => void;
  isLoading?: boolean;
}) => {
  // Incoming requests have `requester`, outgoing have `recipient`
  const otherUser = type === 'incoming' ? request.requester : request.recipient;
  const displayName = otherUser?.name || 'Unknown';

  return (
    <HStack
      w="100%"
      p={4}
      spacing={3}
      bg="white"
      borderBottom="1px solid #F0F0F0"
      align="center">
      <Avatar
        name={displayName}
        src={otherUser?.profile_photo_url || undefined}
        size="md"
      />
      <VStack align="flex-start" spacing={0} flex={1} minW={0}>
        <TextG14
          mt="0"
          fontWeight="600"
          noOfLines={1}>
          {displayName}
        </TextG14>
      </VStack>
      <HStack spacing={2} flexShrink={0}>
        {type === 'incoming' ? (
          <>
            <ActionBtn
              $variant="accept"
              onClick={() => onAccept?.(request.id)}
              disabled={isLoading}>
              Accept
            </ActionBtn>
            <ActionBtn
              $variant="decline"
              onClick={() => onDecline?.(request.id)}
              disabled={isLoading}>
              Decline
            </ActionBtn>
          </>
        ) : (
          <ActionBtn
            $variant="cancel"
            onClick={() => onCancel?.(request.id)}
            disabled={isLoading}>
            Cancel
          </ActionBtn>
        )}
      </HStack>
    </HStack>
  );
};

function MyConnections() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const apiType = activeFilter === 'close_neighbors' || activeFilter === 'shared_plans'
    ? activeFilter
    : undefined;
  const {connections, isLoading: loadingConnections, fetchNextPage, hasNextPage, isFetchingNextPage} = useConnections({
    search: searchQuery || undefined,
    type: apiType,
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {data: incomingRequests = [], isLoading: loadingIncoming} = useIncomingFriendRequests();
  const {data: outgoingRequests = [], isLoading: loadingOutgoing} = useOutgoingFriendRequests();
  const acceptMutation = useAcceptFriendRequest();
  const declineMutation = useDeclineFriendRequest();
  const cancelMutation = useCancelFriendRequest();

  const requestCount = incomingRequests.length + outgoingRequests.length;

  // IntersectionObserver to load next page of connections
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {threshold: 0.1},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Container p={0} maxW="100%" bg="#FAFAFA" minH="100vh">
      {/* Header */}
      <Flex bg="white" px={4} py={3} align="center" borderBottom="1px solid #F0F0F0">
        <Box
          as="button"
          onClick={() => navigate(-1)}
          p={1}
          mr={3}>
          <i className="fi fi-rr-arrow-small-left" style={{fontSize: '20px'}} />
        </Box>
        <TextG16 mt="0" fontWeight="700" flex={1}>
          My Connections
        </TextG16>
      </Flex>

      {/* Search */}
      <Box bg="white" px={4} py={3}>
        <Input
          placeholder="Search connections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          bg="#F4F4F4"
          border="none"
          borderRadius="12px"
          fontSize="14px"
          fontFamily="Gordita"
          _placeholder={{color: '#ABABAB'}}
          _focus={{bg: '#F4F4F4', boxShadow: 'none'}}
        />
      </Box>

      {/* Filter tabs */}
      <HStack bg="white" px={4} pb={3} spacing={2} overflowX="auto">
        <TabButton
          $active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}>
          All
        </TabButton>
        <TabButton
          $active={activeFilter === 'close_neighbors'}
          onClick={() => setActiveFilter('close_neighbors')}>
          Close Neighbors
        </TabButton>
        <TabButton
          $active={activeFilter === 'shared_plans'}
          onClick={() => setActiveFilter('shared_plans')}>
          Shared Plans
        </TabButton>
        <TabButton
          $active={activeFilter === 'requests'}
          onClick={() => setActiveFilter('requests')}>
          Requests
          {requestCount > 0 && (
            <RequestsBadge $active={activeFilter === 'requests'}>
              {requestCount}
            </RequestsBadge>
          )}
        </TabButton>
      </HStack>

      {/* Connection list */}
      <Box mt={2}>
        {activeFilter === 'requests' ? (
          /* Requests tab content */
          (loadingIncoming || loadingOutgoing) ? (
            <Center py={12}>
              <Spinner size="lg" color="#E02F84" />
            </Center>
          ) : (incomingRequests.length === 0 && outgoingRequests.length === 0) ? (
            <Center py={12}>
              <VStack spacing={2}>
                <TextG16 mt="0" color="#757D84" fontWeight="600">
                  No pending requests
                </TextG16>
                <TextG14 mt="0" color="#ABABAB" textAlign="center" px={8}>
                  Friend requests you send or receive will appear here.
                </TextG14>
              </VStack>
            </Center>
          ) : (
            <>
              {incomingRequests.length > 0 && (
                <>
                  <Box px={4} py={3} bg="#FAFAFA">
                    <TextG14 mt="0" fontWeight="700" color="#1A1A1A">
                      Received ({incomingRequests.length})
                    </TextG14>
                  </Box>
                  {incomingRequests.map((req) => (
                    <RequestCard
                      key={req.id}
                      request={req}
                      type="incoming"
                      onAccept={(id) => acceptMutation.mutate(id)}
                      onDecline={(id) => declineMutation.mutate(id)}
                      isLoading={acceptMutation.isLoading || declineMutation.isLoading}
                    />
                  ))}
                </>
              )}
              {outgoingRequests.length > 0 && (
                <>
                  <Box px={4} py={3} bg="#FAFAFA">
                    <TextG14 mt="0" fontWeight="700" color="#1A1A1A">
                      Sent ({outgoingRequests.length})
                    </TextG14>
                  </Box>
                  {outgoingRequests.map((req) => (
                    <RequestCard
                      key={req.id}
                      request={req}
                      type="outgoing"
                      onCancel={(id) => cancelMutation.mutate(id)}
                      isLoading={cancelMutation.isLoading}
                    />
                  ))}
                </>
              )}
            </>
          )
        ) : loadingConnections ? (
          <Center py={12}>
            <Spinner size="lg" color="#E02F84" />
          </Center>
        ) : connections.length > 0 ? (
          <>
            {connections.map((connection) => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                onPress={() => navigate(`/neighbor/${connection.id}`)}
              />
            ))}
            <div ref={sentinelRef} style={{height: 1}} />
            {isFetchingNextPage && (
              <Center py={4}>
                <Spinner size="md" color="#E02F84" />
              </Center>
            )}
          </>
        ) : (
          <Center py={12}>
            <VStack spacing={2}>
              <TextG16 mt="0" color="#757D84" fontWeight="600">
                {searchQuery ? 'No results found' : 'No connections yet'}
              </TextG16>
              <TextG14 mt="0" color="#ABABAB" textAlign="center" px={8}>
                {searchQuery
                  ? 'Try a different search term'
                  : 'Start connecting with your neighbors!'}
              </TextG14>
            </VStack>
          </Center>
        )}
      </Box>
    </Container>
  );
}

export default MyConnections;
